import React from "react";
import { Link, useLocation } from "react-router-dom";

interface PageHeaderProps {
  title: string;
  subTitle?: string;
}

// Helper to format path segment into readable label
const formatSegment = (segment: string): string => {
  // Handle special cases
  if (segment === "new") return "Create";
  // Convert kebab-case to Title Case
  return segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const PageHeader: React.FC<PageHeaderProps> = ({ title, subTitle }) => {
  const location = useLocation();

  // Build dynamic breadcrumbs from all path segments
  const pathSegments = location.pathname.split("/").filter(Boolean);

  const crumbs = pathSegments
    .map((segment, index) => {
      // Skip segments after 'new' (e.g., the route param)
      if (index > 0 && pathSegments[index - 1] === "new") return null;

      const isNewSegment = segment === "new";
      const isLast = index === pathSegments.length - 1 || isNewSegment;

      return {
        key: `${segment}-${index}`,
        label: formatSegment(segment),
        path: "/" + pathSegments.slice(0, index + 1).join("/"),
        isLast,
      };
    })
    .filter(Boolean) as Array<{
    key: string;
    label: string;
    path: string;
    isLast: boolean;
  }>;

  return (
    <header>
      <nav aria-label="breadcrumb">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs text-zinc-500">
          <li>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1 text-zinc-500 no-underline transition-colors hover:text-zinc-900"
            >
              <i className="bi bi-speedometer2" />
              Home
            </Link>
          </li>
          {crumbs.map((crumb) => (
            <li key={crumb.key} className="flex items-center gap-1.5">
              <i className="bi bi-chevron-right text-[10px] text-zinc-300" />
              {crumb.isLast ? (
                <span aria-current="page" className="text-zinc-500">
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.path}
                  className="text-zinc-500 no-underline transition-colors hover:text-zinc-900"
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>

      <h1 className="mt-2 text-2xl font-semibold tracking-tight text-zinc-900">
        {title}
        {/* Several pages pass the same string as both title and subTitle
            (e.g. "Orders" / "Orders"); showing it twice just reads as a bug. */}
        {subTitle && subTitle.toLowerCase() !== title.toLowerCase() && (
          <span className="ml-2 text-base font-normal text-zinc-500">
            {subTitle}
          </span>
        )}
      </h1>
    </header>
  );
};

export default PageHeader;
